// src/TherapistDashboard.js
import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  User, 
  FileText, 
  LogOut, 
  Settings, 
  Menu, 
  X, 
  ChevronRight,
  MessageSquare,
  Award,
  Users
} from 'lucide-react';
import TherapistProfile from './dashboard/TherapistProfile';
import TherapistQuestions from './dashboard/TherapistQuestions';

const TherapistDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Get user from localStorage or fetch from API
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
          throw new Error('Not authenticated');
        }
        
        const userData = JSON.parse(storedUser);
        
        // Optionally fetch fresh user data from API
        const response = await fetch('/api/auth/me', {
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        
        if (!response.ok) {
          // If API fails, still use stored data but log the error
          console.error('Failed to fetch fresh user data');
          setUser(userData);
        } else {
          const freshData = await response.json();
          setUser(freshData.user);
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(freshData.user));
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Not authenticated. Please log in.');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      // Clear localStorage
      localStorage.removeItem('user');
      
      // Redirect to login
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  // Get current section from path
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.includes('/questions')) return 'questions';
    return 'profile';
  };
  
  // Set page title based on section
  const getPageTitle = () => {
    const section = getCurrentSection();
    switch (section) {
      case 'questions':
        return 'Your Questions & Answers';
      default:
        return 'Your Profile';
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => navigate('/login')}
            className="w-full p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-blue-50 flex flex-col">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="mr-4 p-1 hover:bg-blue-700 rounded-full block lg:hidden"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="flex items-center">
              <FileText className="mr-2" size={24} />
              <h1 className="text-xl font-bold">Therapist Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center">
            {user && (
              <div className="mr-4 hidden md:block">
                <span className="font-medium">Dr. {user.firstName} {user.lastName}</span>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center p-2 hover:bg-blue-700 rounded"
              title="Logout"
            >
              <LogOut size={20} />
              <span className="ml-2 hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-grow flex">
        {/* Sidebar - Desktop */}
        <aside className="w-64 bg-white shadow-md hidden lg:block">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-800">Navigation</h2>
          </div>
          <nav className="py-4">
            <ul>
              <li>
                <Link
                  to="/therapist-dashboard"
                  className={`flex items-center px-4 py-3 ${
                    getCurrentSection() === 'profile'
                      ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <User size={20} className="mr-2" />
                  <span>Profile</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/therapist-dashboard/questions"
                  className={`flex items-center px-4 py-3 ${
                    getCurrentSection() === 'questions'
                      ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <MessageSquare size={20} className="mr-2" />
                  <span>Your Questions</span>
                </Link>
              </li>
              
              {/* If the therapist is "rick" or "dave", show the insights link */}
              {user && (user.firstName.toLowerCase() === 'rick' || user.firstName.toLowerCase() === 'dave') && (
                <li>
                  <Link
                    to={`/${user.firstName.toLowerCase()}`}
                    className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100"
                  >
                    <Award size={20} className="mr-2" />
                    <span>Insights Platform</span>
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </aside>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div 
              className="absolute inset-0 bg-gray-800 bg-opacity-50"
              onClick={() => setMenuOpen(false)}
            ></div>
            <div className="absolute top-0 left-0 w-64 h-full bg-white shadow-lg">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="font-semibold text-gray-800">Navigation</h2>
                <button 
                  onClick={() => setMenuOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="py-4">
                <ul>
                  <li>
                    <Link
                      to="/therapist-dashboard"
                      className={`flex items-center px-4 py-3 ${
                        getCurrentSection() === 'profile'
                          ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <User size={20} className="mr-2" />
                      <span>Profile</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/therapist-dashboard/questions"
                      className={`flex items-center px-4 py-3 ${
                        getCurrentSection() === 'questions'
                          ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      onClick={() => setMenuOpen(false)}
                    >
                      <MessageSquare size={20} className="mr-2" />
                      <span>Your Questions</span>
                    </Link>
                  </li>
                  
                  {/* If the therapist is "rick" or "dave", show the insights link */}
                  {user && (user.firstName.toLowerCase() === 'rick' || user.firstName.toLowerCase() === 'dave') && (
                    <li>
                      <Link
                        to={`/${user.firstName.toLowerCase()}`}
                        className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100"
                        onClick={() => setMenuOpen(false)}
                      >
                        <Award size={20} className="mr-2" />
                        <span>Insights Platform</span>
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-grow bg-blue-50 p-6">
          <div className="container mx-auto">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{getPageTitle()}</h2>
              <p className="text-gray-600 mt-1">
                {getCurrentSection() === 'profile' 
                  ? 'Manage your professional profile information'
                  : 'Review your questionnaire responses'
                }
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <Routes>
                <Route path="/" element={<TherapistProfile user={user} />} />
                <Route path="/questions" element={<TherapistQuestions user={user} />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
        <div className="container mx-auto">
          Mindaera | Therapist Portal | Internal Dev Prototype
        </div>
      </footer>
    </div>
  );
};

export default TherapistDashboard;